import { Fade, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { Ellipsis } from 'lucide-react';
import * as React from 'react';
import { useState } from 'react';
import IconButton from '../../../../../../../components/v2/ui/IconButton/IconButton';
import Menu from '../../../../../../../components/v2/ui/Menu/Menu';
import MenuItem from '../../../../../../../components/v2/ui/Menu/MenuItem';
import Typography from '../../../../../../../components/v2/ui/Typography/Typography';
import LangEnum from '../../../../../../../lang/LangEnum';
import { prevent } from '../../../../../../../utils/generics';
import { useTranslation } from '../../../../../../translation/TranslationContext';
import { useCatalogCardActionsStyles } from '../styles/catalogCardActions.styles';

export type CatalogActionsProps = {
  label: LangEnum;
  icon: React.ElementType;
  onClick: () => void;
  isQuickAction?: boolean;
};

const CatalogActions = ({ actions }: { actions: CatalogActionsProps[] }) => {
  const { classes } = useCatalogCardActionsStyles();
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const quickActions = actions.filter((action) => action.isQuickAction);
  const moreActions = actions.filter((action) => !action.isQuickAction);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    prevent(event);
    setAnchorEl(event.currentTarget);
  };

  const handleAction = (event: React.MouseEvent<HTMLElement>, action: () => void) => {
    prevent(event);
    action();
    setAnchorEl(null);
  };

  return (
    <Stack direction="row" spacing={1.5}>
      {quickActions.map((action) => (
        <IconButton
          key={action.label}
          icon={action.icon}
          ariaLabel={translate(action.label)}
          className={classes.iconButton}
          onClick={(event) => {
            prevent(event);
            action.onClick();
          }}
        />
      ))}
      {moreActions.length > 0 && (
        <>
          <IconButton
            className={classes.iconButton}
            onClick={(event: any) => handleClick(event)}
            icon={Ellipsis}
          />
          <Menu
            anchorEl={anchorEl}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={open}
            onClose={(e: React.SyntheticEvent<Element, Event>) => {
              prevent(e);
              setAnchorEl(null);
            }}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            slotProps={{
              transition: Fade,
            }}
          >
            {moreActions.map((action) => (
              <MenuItem key={action.label} onClick={(e) => handleAction(e, action.onClick)}>
                <ListItemIcon>
                  <action.icon />
                </ListItemIcon>
                <ListItemText>
                  <Typography variant="body2">{translate(action.label)}</Typography>
                </ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
};

export default CatalogActions;
